import { useEffect } from 'react';
import { useAlert } from 'react-alert';
import { Redirect } from 'react-router-dom';
import I18n from 'i18n-js';

import { resetPassword } from '@lootboy/lib/actions/AuthActions';
import {
  ENewPasswordFormField,
  useCreateNewPasswordForm,
} from '@lootboy/lib/forms/useCreateNewPasswordForm';
import { NOOP } from '@lootboy/lib/utils';

import Alert from '~/components/Alert';
import { PasswordInput } from '~/components/atoms/FormControls/PasswordInput';
import { ScrollContainer } from '~/components/atoms/ScrollContainer';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import { MenuViewHeader } from '~/components/MenuViewHeader';
import { AuthViewHeadline } from '~/components/molecules/AuthViewHeadline';
import { ValidationHints } from '~/components/molecules/ValidationHints';
import { passwordValidationSchema } from '~/components/molecules/ValidationHints/validationSchema';

import { useURLSearchParams } from '~/hooks/useURLSearchParams';
import { useAppDispatch } from '~/redux/store/useAppDispatch';
import {
  PASSWORD_RESET_PATH,
  REQUEST_PASSWORD_RESET_PATH,
} from '~/routes/paths';
import { useAppHistory, useAppLocation } from '~/utilities/routing';

import styles from './PasswordResetView.module.scss';

const PASSWORD_RESET_FORM_ID = 'password_reset_form';

/**
 *
 * this screen can be opened by navigating from `PasswordResetCodePopup` (/password-reset-code)
 * or by using the link from the reset password email that is sent after submitting a form in
 * `RequestPasswordResetPopup` (/request-password-reset)
 */
export function PasswordResetPopup(): JSX.Element {
  const alert = useAlert();
  const dispatch = useAppDispatch();
  const history = useAppHistory();

  // if user entered the email code manually the token will be stored in location state
  const { state } = useAppLocation<typeof PASSWORD_RESET_PATH>();
  // if user used the email link the token will be stored in the URL query param
  const queryParamToken = useURLSearchParams('token');

  const token = state?.token ?? queryParamToken;

  const {
    watch,
    register,
    formState: { errors },
    onFormSubmit,
    isSubmitEnabled,
    trigger,
  } = useCreateNewPasswordForm(async (values) => {
    if (!token) {
      return;
    }

    try {
      await dispatch(resetPassword(token, values.password));
      // the redirect to the screen after reset is handled by the  `WelcomeScreen`
    } catch (error) {
      alert.error(
        <Alert
          body={(error as { message?: string })?.message ?? ''}
          title={I18n.t('ResetPasswordView_reset_failed')}
        />,
      );
    }
  });

  const password = watch(ENewPasswordFormField.NEW_PASSWORD);
  const repeatPassword = watch(ENewPasswordFormField.REPEAT_NEW_PASSWORD);

  // validate the `repeatPassword` field when `password` field changes
  useEffect(() => {
    if (password && repeatPassword) {
      trigger(ENewPasswordFormField.REPEAT_NEW_PASSWORD).catch(NOOP);
    }
  }, [password, trigger, repeatPassword]);

  if (!token) {
    return <Redirect to={REQUEST_PASSWORD_RESET_PATH} />;
  }

  return (
    <>
      <MenuViewHeader
        title={I18n.t('MenuView_login_bc')}
        onBack={() => {
          history.push(REQUEST_PASSWORD_RESET_PATH);
        }}
      />
      <ScrollContainer>
        <section className={styles.container}>
          <AuthViewHeadline
            headline={I18n.t('MenuView_reset_headline')}
            subHeadline={I18n.t('MenuView_reset_sub')}
          />
          <form
            onSubmit={onFormSubmit}
            id={PASSWORD_RESET_FORM_ID}
            className={styles.form}
          >
            <PasswordInput
              {...register(ENewPasswordFormField.NEW_PASSWORD)}
              error={errors[ENewPasswordFormField.NEW_PASSWORD]?.message}
              label={I18n.t('MenuView_reset_setpassword')}
              id={ENewPasswordFormField.NEW_PASSWORD}
              placeholder={I18n.t('MenuView_reset_setpassword_placeholder')}
            />
            <PasswordInput
              {...register(ENewPasswordFormField.REPEAT_NEW_PASSWORD)}
              error={errors[ENewPasswordFormField.REPEAT_NEW_PASSWORD]?.message}
              label={I18n.t('MenuView_reset_setpassword_repeat')}
              id={ENewPasswordFormField.REPEAT_NEW_PASSWORD}
              placeholder={I18n.t('MenuView_reset_repeat_placeholder')}
            />
            <ValidationHints
              constraints={passwordValidationSchema}
              value={password}
            />
          </form>
        </section>
      </ScrollContainer>
      <footer className={styles.footer}>
        <StyledButton
          className={styles.footerButton}
          variant={EStyledButtonVariant.BLUE}
          disabled={!isSubmitEnabled}
          type="submit"
          form={PASSWORD_RESET_FORM_ID}
        >
          {I18n.t('confirm')}
        </StyledButton>
      </footer>
    </>
  );
}
